import { type SanityReference } from '@sanity/client'

import { type SanityFrameBuilderType } from '@frame-builder/data/sanity/queries/types'
import { type SanityFrameBuilderDefaultFrame } from '@frame-builder/data/sanity/queries/types/default-frame'
import { type SanityFrameBuilderSettings } from '@frame-builder/data/sanity/queries/types/settings'
import { getSanityImageUrl } from './image'
import {
  type FrameBuilderConfigurationWithLimits,
  type FrameBuilderState,
} from './types'

/**
 * Gets minimum mount size.
 */
export const getMinimumPassepartoutSize = (
  settings: SanityFrameBuilderSettings,
) => {
  return settings.minimumPassepartoutSize ?? 1
}

/**
 * Gets maximum mount size.
 */
export const getMaximumPassepartoutSize = (
  settings: SanityFrameBuilderSettings,
) => {
  return settings.maximumPassepartoutSize ?? 10
}

/**
 * Gets frame builder default state from default frame query results.
 */
export const getFrameBuilderDefaultState = (
  frameBuilderType: SanityFrameBuilderType,
  frameBuilderConfiguration: FrameBuilderConfigurationWithLimits,
  settings: SanityFrameBuilderSettings,
  defaultFrame: SanityFrameBuilderDefaultFrame | null,
  defaultPassepartoutColor?: SanityReference,
  defaultPassepartoutWidth?: number,
  defaultPassepartoutHeight?: number,
) => {
  const frameBuilderState: FrameBuilderState = {
    artwork: {
      width: 50,
      height: 50,
    },
    passepartout: {},
  }

  if (defaultFrame?.width) {
    frameBuilderState.artwork.width = defaultFrame.width
  }

  if (defaultFrame?.height) {
    frameBuilderState.artwork.height = defaultFrame.height
  }

  if (frameBuilderType === 'frame' || frameBuilderType === 'passepartout') {
    const defaultPreviewImageUrl = getSanityImageUrl(
      frameBuilderType === 'passepartout'
        ? defaultFrame?.passepartoutPreviewImage
        : defaultFrame?.previewImage,
    )
    frameBuilderState.artwork.image = defaultPreviewImageUrl
      ? {
          url: defaultPreviewImageUrl,
          name: '',
        }
      : undefined
    frameBuilderState.artwork.backgroundColor = {
      current: defaultFrame?.backgroundColor?.hex,
      predefinedColors: defaultFrame?.backgroundColorPresets?.map(
        (backgroundColorPreset) => backgroundColorPreset.hex,
      ),
    }

    const defaultPassepartoutColorId =
      defaultPassepartoutColor?._ref ?? defaultFrame?.passepartoutColor?._ref
    frameBuilderState.passepartout.color =
      frameBuilderConfiguration.passepartoutColors.find((passepartoutColor) => {
        return passepartoutColor._id === defaultPassepartoutColorId
      })
    const minimumPassepartoutSize = getMinimumPassepartoutSize(settings)
    const maximumPassepartoutSize = getMaximumPassepartoutSize(settings)
    frameBuilderState.passepartout.width = Math.min(
      Math.max(
        defaultPassepartoutWidth ?? defaultFrame?.passepartoutWidth ?? 0,
        minimumPassepartoutSize,
      ),
      maximumPassepartoutSize,
    )
    frameBuilderState.passepartout.height = Math.min(
      Math.max(
        defaultPassepartoutHeight ?? defaultFrame?.passepartoutHeight ?? 0,
        minimumPassepartoutSize,
      ),
      maximumPassepartoutSize,
    )
  }

  if (frameBuilderType === 'frame') {
    frameBuilderState.profile = frameBuilderConfiguration.profiles.find(
      (profile) => {
        return profile._id === defaultFrame?.profileTexture?.profile?._ref
      },
    )
    frameBuilderState.texture = frameBuilderConfiguration.textures.find(
      (texture) => {
        return texture._id === defaultFrame?.profileTexture?.texture?._ref
      },
    )
    frameBuilderState.glass = frameBuilderConfiguration.glass.find((glass) => {
      return glass._id === defaultFrame?.glass?._ref
    })
    frameBuilderState.spacerSize = frameBuilderConfiguration.spacerSizes.find(
      (spacerSize) => {
        return spacerSize._id === defaultFrame?.spacerSize?._ref
      },
    )
    frameBuilderState.spacerColor = frameBuilderConfiguration.spacerColors.find(
      (spacerColor) => {
        return spacerColor._id === defaultFrame?.spacerColor?._ref
      },
    )
  }

  return frameBuilderState
}
